import React, { ReactElement, useEffect } from 'react';

import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Card,
  CardContent,
  Grid,
  ThemeProvider,
  Typography,
} from '@mui/material';

import { useSetFeatureId } from '../../../../../../shared/hooks/use-set-feature-id/use-set-feature-id.hook';
import { useAppDispatch } from '../../../../../../store/hooks';
import { useGetReviewStatisticsSelector } from '../../hooks/use-get-review-statistics-selector/use-get-review-statistics-selector.hook';
import { setAppId } from '../../store/slices/slices/reviews.slice';
import { getReviewsStats } from '../../thunks/get-reviews-stats.thunk';
import { AvgRatingsChart } from '../avg-ratings-chart/avg-ratings-chart';
import { RatingsBreakdownChart } from '../ratings-breakdown-chart/ratings-breakdown-chart';
import { statsTheme } from './theme/stats.theme';

const ReviewStatistics = (): ReactElement => {
  const dispatch = useAppDispatch();

  const { stats, appId } = useGetReviewStatisticsSelector();

  useSetFeatureId(setAppId, parseInt(appId as string, 10));

  useEffect(() => {
    if (appId) {
      dispatch(getReviewsStats(+appId));
    }
  }, [appId]);

  return (
    <ThemeProvider theme={statsTheme}>
      <Box sx={{ display: 'flex', margin: '48px 0', width: '100%' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" data-testid="statistics">
              <CardContent>
                <Box sx={{ mt: '36px' }}>
                  <Typography component="span" className="overall-rating">
                    {stats?.overAllRating.toPrecision(3)}
                  </Typography>
                  <StarIcon color="primary" />
                  <Typography className="total-ratings">
                    {`out of ${stats?.totalRatings} ratings`}
                  </Typography>
                </Box>
                <Typography className="total-reviews">
                  {stats?.totalReviews}
                </Typography>
                <Typography>Total Reviews</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" data-testid="ratings-breakdown">
              <CardContent>
                <Typography className="breakdown-title">
                  Ratings breakdown
                </Typography>
                <RatingsBreakdownChart stats={stats} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" data-testid="avg-ratings">
              <CardContent>
                <Typography className="breakdown-title">
                  Avg ratings over time
                </Typography>
                <AvgRatingsChart stats={stats} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default ReviewStatistics;
